import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import { Community } from "../../types/community/community.type";
import { User } from "../../types/user/minimal-user.type";
import { Button } from "../../_components/button.component";
import { ErrorDiv } from "../../_components/error.component";
import { CommunityService } from "../../_service";
import { setCommunity } from "../../_store/_actions/community.actions";
import { useAppService } from "../../hooks/use-app-service";

export const COMMUNITY_THEME_SELECTOR_PAGE_ROUTE = "/settings/selecttheme";

interface Props {
  user: User;
  community: Community;
  addToast?: any;
}

function CommunityThemeSelectorComponent({ user, community, addToast }: Props) {
  const { analyticsService } = useAppService();

  const [primaryColor, setPrimaryColor] = useState("#0445FF");
  const [onPrimaryColor, setOnPrimaryColor] = useState("#FFFFFF");
  const [theme, setTheme] = useState("light");

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [isLoadingColors, setisLoadingColors] = useState(false);

  // fetch user posts and details
  useEffect(() => {
    if (community.id) {
      // get user posts
      setisLoadingColors(true);
      CommunityService.getCommunityThemeData(user, community.id)
        .then(({ themeData }) => {
          setPrimaryColor(themeData.primaryColor);
          setOnPrimaryColor(themeData.onPrimaryColor);
          setTheme(themeData.backgroundColor === "#141519" ? "dark" : "light");
        })
        .finally(() => {
          setisLoadingColors(false);
        });
    }
  }, [community.id, user]); // no need to track user

  return (
    <div className="CommunityThemeSelector Card rounded shadow theme-bg-surface">
      {/* headers */}
      <div className="pl-4 pr-2 py-4  border-b theme-border-default flex items-center justify-between">
        <span className="font-bold theme-text-heading-2">Community Theme</span>
      </div>
      <div className="card p-5 pt-0 theme-text-heading-2 mt-2">
        <div className="mt-2">
          {/* theme selectors */}
          <div className="my-5">
            <div className="font-bold theme-text-heading-2">Select Theme</div>
            <div className="flex py-4">
              {/* light theme */}
              <div
                className={
                  "theme-preview mr-5" + (theme === "light" ? " selected" : "")
                }
                onClick={(e) => {
                  setTheme("light");
                }}>
                <img
                  src="/assets/vectors/theme/light-theme.svg"
                  alt="Light Theme"
                  className="unselected-icon"
                />
                <img
                  src="/assets/vectors/theme/light-theme-selected.svg"
                  alt="Light Theme"
                  className="selected-icon"
                />
              </div>
              {/* dark theme */}
              <div
                className={
                  "theme-preview mr-5" + (theme === "dark" ? " selected" : "")
                }
                onClick={(e) => {
                  setTheme("dark");
                }}>
                <img
                  src="/assets/vectors/theme/dark-theme.svg"
                  alt="Light Theme"
                  className="unselected-icon dark border theme-border-default rounded"
                />
                <img
                  src="/assets/vectors/theme/dark-theme-selected.svg"
                  alt="Light Theme"
                  className="selected-icon border border-transparent rounded"
                />
              </div>
            </div>
          </div>
          {/* color pickers */}
          <div className="flex flex-col sm:flex-row mb-6">
            {/* Primary Color */}
            <div className="flex-grow">
              <div className="font-bold">Select Brand Color</div>
              <div className="theme-subtitle-1 text-xs">
                Customize your button color to match your brand.
              </div>
            </div>
            <ColorPicker
              label={"Primary Color"}
              color={primaryColor}
              isLoadingColors={isLoadingColors}
              onChange={(e) => {
                setPrimaryColor(e.target.value);
              }}
            />
          </div>
          <div className="flex flex-col sm:flex-row mb-6">
            {/* On Primary Color */}
            <div className="flex-grow">
              <div className="font-bold">Select On Button Text Color</div>
              <div className="theme-subtitle-1 text-xs">
                Suggestion: Keep inverted contrast against brand color
              </div>
            </div>
            <ColorPicker
              label={"On Primary Color"}
              color={onPrimaryColor}
              isLoadingColors={isLoadingColors}
              onChange={(e) => {
                setOnPrimaryColor(e.target.value);
              }}
            />
          </div>
        </div>
        {/* divider */}
        <div className="border-b theme-border-default mt-16 mb-2" />
        {/* footer buttons */}
        <div className="flex justify-between">
          {/* cancel button */}
          <Button label="Cancel" className="py-2 mt-5" flat />
          {/* submit button */}
          <Button
            className="py-2 mt-5"
            onClick={() => {
              // check if form can be submitted
              setIsLoading(true);

              let themeData = {
                primaryColor,
                onPrimaryColor,
                theme,
              };
              // call api to create community
              CommunityService.toggleCommunityThemeData(
                user,
                community.id,
                themeData
              )
                .then((response) => {
                  setIsLoading(false);
                  addToast("Theme Colors updated", "", ToastTypes.success);
                  window.location.reload();
                })
                .catch((error) => {
                  console.log(error);
                  setIsLoading(false);
                  if (error && error.response && error.response.data.message) {
                    if (error.response.data.themeData.primaryColor) {
                      setErrorMessage(
                        error.response.data.errors.primaryColor[0]
                      );
                    }
                    if (error.response.data.themeData.onPrimaryColor) {
                      setErrorMessage(
                        error.response.data.errors.onPrimaryColor[0]
                      );
                    }
                    if (error.response.data.errors.address) {
                      setErrorMessage(
                        error.response.data.errors.secondaryColor[0]
                      );
                    }
                  } else {
                    setErrorMessage("Something went wrong!");
                  }
                });
            
                analyticsService.track("theme-updated")
            }}
            isLoading={isLoading}
            label="Save"
          />
        </div>
        <ErrorDiv error={errorMessage} />
      </div>
    </div>
  );
}

interface ColorPickerProps {
  label: string;
  color: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoadingColors?: boolean;
}
/**
 * Component to update a picture resource
 * @param {*} param0
 * @returns
 */

function ColorPicker({
  label,
  color,
  onChange = (e) => {},
  isLoadingColors = false,
}: ColorPickerProps) {
  const colorInputRef = React.useRef<any>(null);

  if (isLoadingColors == true || false) {
    return (
      <div className="flex items-center animate-pulse ">
        <div className="h-8 flex-grow rounded border theme-border-default mr-4 py-2 px-4 w-32 theme-bg-default" />
        <div className="h-8 w-8 theme-bg-default rounded shadow" />
      </div>
    );
  }

  return (
    <div className="flex items-center">
      {/* add pattern for color */}
      <input
        type="text"
        className="flex-grow rounded border theme-border-default mr-4 py-2 px-4 w-32 theme-bg-default"
        value={color}
        onChange={onChange}
      />
      <div
        style={{ backgroundColor: color, width: "36px", height: "36px" }}
        className="rounded shadow"
        onClick={(e) => {
          if (colorInputRef.current) colorInputRef.current.click();
        }}></div>
      <input
        type="color"
        ref={(ref) => {
          colorInputRef.current = ref;
        }}
        className="h-0 w-0"
        style={{ visibility: "hidden" }}
        name="favcolor"
        value={color}
        onChange={onChange}
      />
    </div>
  );
}

const CommunityThemeSelectorPage = withToast(
  connect(
    (s: any) => ({
      user: s.auth,
      community: s.community,
    }),
    (d: any) =>
      bindActionCreators(
        {
          onCommunityUpdate: setCommunity,
        },
        d
      )
  )(CommunityThemeSelectorComponent)
);

export default CommunityThemeSelectorPage;
